// ContentSwipeFilePage.tsx — Content Swipe File (Structured mini-app)
//
// A marketing swipe file: browse real competitor / exemplar content to analyze
// it. Three layers:
//   1. Company grid       — pick a company.
//   2. Content card grid  — thumbnail + title + content_type tag, scannable.
//   3. Lightbox modal     — image, full copy_text (line breaks preserved),
//                           content_type, and a link to the source.
//
// Structured path: bespoke React + useApi(). No markdown renderer. The T
// palette matches the rest of the dashboard (see ArchitectureBlocks).

import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ExternalLink, ImageOff, Loader2, X } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type Company = {
  id: string;
  name: string;
  slug: string;
  url: string | null;
  category: string | null;
  why: string | null;
  logo_url: string | null;
  sort_order: number | null;
  created_at: string;
};

type Content = {
  id: string;
  company_slug: string;
  title: string;
  content_type: string | null;
  source_url: string | null;
  image_url: string | null;
  copy_text: string | null;
  notes: string | null;
  sort_order: number | null;
  created_at: string;
};

type ApiResponse = { companies: Company[]; content: Content[] };

// ── Small presentational pieces ─────────────────────────────────────────────

function TypeTag({ type }: { type: string | null }) {
  if (!type) return null;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: T.skyDark,
        background: T.skySoft,
        border: `1px solid ${T.sky}`,
        padding: "2px 9px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {type}
    </span>
  );
}

function Thumb({
  src,
  alt,
  height = 150,
}: {
  src: string | null;
  alt: string;
  height?: number;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div
        style={{
          height,
          background: T.bg2,
          borderBottom: `1px solid ${T.line}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.ink3,
          gap: 8,
          fontSize: 12,
        }}
      >
        <ImageOff style={{ width: 16, height: 16 }} />
        No image
      </div>
    );
  }
  return (
    <div
      style={{
        height,
        background: T.bg2,
        borderBottom: `1px solid ${T.line}`,
        overflow: "hidden",
      }}
    >
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </div>
  );
}

// ── Lightbox ────────────────────────────────────────────────────────────────

function Lightbox({ item, onClose }: { item: Content; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(16,24,40,0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 24px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 720,
          background: T.white,
          borderRadius: 14,
          border: `1px solid ${T.line}`,
          boxShadow: "0 24px 60px rgba(16,24,40,0.28)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${T.line}`,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <TypeTag type={item.content_type} />
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: T.ink,
                margin: 0,
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
              }}
            >
              {item.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              border: `1px solid ${T.line}`,
              background: T.bg,
              borderRadius: 8,
              padding: 6,
              cursor: "pointer",
              color: T.ink2,
              display: "inline-flex",
            }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Image */}
        {item.image_url && (
          <Thumb src={item.image_url} alt={item.title} height={320} />
        )}

        {/* Copy */}
        <div style={{ padding: "24px 28px 8px" }}>
          {item.copy_text ? (
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.7,
                color: T.ink,
                whiteSpace: "pre-wrap",
                margin: 0,
                fontFamily: "Inter, sans-serif",
              }}
            >
              {item.copy_text}
            </p>
          ) : (
            <p style={{ fontSize: 14, color: T.ink3, margin: 0 }}>
              No copy captured for this item.
            </p>
          )}

          {item.notes && (
            <div
              style={{
                marginTop: 20,
                padding: "14px 16px",
                background: T.amberSoft,
                border: `1px solid ${T.amber}`,
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: T.amber,
                  marginBottom: 6,
                }}
              >
                Notes
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: T.ink2, margin: 0, whiteSpace: "pre-wrap" }}>
                {item.notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer / source link */}
        <div style={{ padding: "16px 28px 24px" }}>
          {item.source_url ? (
            <a
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: T.accent,
                color: T.white,
                fontSize: 14,
                fontWeight: 600,
                padding: "10px 18px",
                borderRadius: 8,
                textDecoration: "none",
              }}
            >
              View source
              <ExternalLink style={{ width: 15, height: 15 }} />
            </a>
          ) : (
            <span style={{ fontSize: 13, color: T.ink3 }}>No source link.</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function ContentSwipeFilePage() {
  const api = useApi();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCompany, setActiveCompany] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<Content | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/swipe-file");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ApiResponse;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentByCompany = useMemo(() => {
    const map = new Map<string, Content[]>();
    for (const c of data?.content ?? []) {
      const arr = map.get(c.company_slug) ?? [];
      arr.push(c);
      map.set(c.company_slug, arr);
    }
    return map;
  }, [data]);

  const company = useMemo(
    () => data?.companies.find((c) => c.slug === activeCompany) ?? null,
    [data, activeCompany],
  );

  const shell = (children: React.ReactNode) => (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        boxSizing: "border-box",
        padding: "40px 48px 80px",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
    </div>
  );

  if (error) {
    return shell(
      <div
        style={{
          padding: "16px 20px",
          color: T.red,
          background: T.redSoft,
          border: `1px solid ${T.red}`,
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        Failed to load marketing research: {error}
      </div>,
    );
  }

  if (data === null) {
    return shell(
      <div
        style={{
          padding: 24,
          fontSize: 14,
          color: T.ink3,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
        Loading…
      </div>,
    );
  }

  // ── Company detail (content grid) ─────────────────────────────────────────
  if (company) {
    const items = contentByCompany.get(company.slug) ?? [];
    return shell(
      <>
        <button
          onClick={() => setActiveCompany(null)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "transparent",
            border: "none",
            color: T.accent,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            padding: 0,
            marginBottom: 20,
          }}
        >
          <ArrowLeft style={{ width: 15, height: 15 }} />
          All companies
        </button>

        <div style={{ marginBottom: 28 }}>
          {company.category && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: T.skyDark,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              {company.category}
            </div>
          )}
          <h1
            style={{
              fontSize: 32,
              fontWeight: 800,
              color: T.ink,
              margin: "0 0 10px",
              letterSpacing: "-0.02em",
            }}
          >
            {company.name}
          </h1>
          {company.why && (
            <p style={{ fontSize: 16, color: T.ink2, lineHeight: 1.6, maxWidth: 760, margin: "0 0 8px" }}>
              {company.why}
            </p>
          )}
          {company.url && (
            <a
              href={company.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                color: T.accent,
                textDecoration: "none",
              }}
            >
              {company.url.replace(/^https?:\/\//, "")}
              <ExternalLink style={{ width: 13, height: 13 }} />
            </a>
          )}
        </div>

        {items.length === 0 ? (
          <div style={{ padding: 24, fontSize: 14, color: T.ink3 }}>
            No content captured for this company yet.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveItem(item)}
                style={{
                  textAlign: "left",
                  padding: 0,
                  cursor: "pointer",
                  background: T.white,
                  border: `1px solid ${T.line}`,
                  borderRadius: 12,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  transition: "box-shadow 0.15s, transform 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 10px 28px rgba(16,24,40,0.12)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <Thumb src={item.image_url} alt={item.title} />
                <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                  <TypeTag type={item.content_type} />
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: T.ink,
                      lineHeight: 1.35,
                    }}
                  >
                    {item.title}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {activeItem && <Lightbox item={activeItem} onClose={() => setActiveItem(null)} />}
      </>,
    );
  }

  // ── Company grid (landing) ────────────────────────────────────────────────
  return shell(
    <>
      <div style={{ textAlign: "center", padding: "32px 20px 40px", marginBottom: 12 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: T.skyDark,
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          MyJarvis · Marketing
        </div>
        <h1
          style={{
            fontSize: 34,
            fontWeight: 800,
            color: T.ink,
            margin: "0 0 14px",
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
          }}
        >
          Marketing Research
        </h1>
        <p style={{ fontSize: 16, color: T.ink2, lineHeight: 1.65, maxWidth: 720, margin: "0 auto" }}>
          Real competitor and exemplar marketing content, captured for analysis. Pick a company to
          browse its swipes; click any card for the full copy and source.
        </p>
      </div>

      {data.companies.length === 0 ? (
        <div style={{ padding: 24, fontSize: 14, color: T.ink3, textAlign: "center" }}>
          No companies yet.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 20,
          }}
        >
          {data.companies.map((c) => {
            const count = contentByCompany.get(c.slug)?.length ?? 0;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCompany(c.slug)}
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  background: T.white,
                  border: `1px solid ${T.line}`,
                  borderRadius: 12,
                  padding: "20px 20px 18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  minHeight: 140,
                  transition: "box-shadow 0.15s, transform 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 10px 28px rgba(16,24,40,0.12)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {c.logo_url ? (
                    <img
                      src={c.logo_url}
                      alt={c.name}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        objectFit: "contain",
                        background: T.bg2,
                        border: `1px solid ${T.line}`,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: T.skySoft,
                        color: T.skyDark,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 18,
                      }}
                    >
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 800,
                      color: T.ink,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {c.name}
                  </div>
                </div>

                {c.category && (
                  <div style={{ marginTop: -2 }}>
                    <TypeTag type={c.category} />
                  </div>
                )}

                {c.why && (
                  <p
                    style={{
                      fontSize: 13,
                      color: T.ink2,
                      lineHeight: 1.55,
                      margin: 0,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {c.why}
                  </p>
                )}

                <div
                  style={{
                    marginTop: "auto",
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.ink3,
                  }}
                >
                  {count} {count === 1 ? "item" : "items"}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </>,
  );
}

(ContentSwipeFilePage as unknown as { path: string }).path = "/content-swipe-file";
